import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Tag, X } from 'lucide-react';
import { auth } from '../../firebase/config';

const getAuthToken = async () => {
    const user = auth.currentUser;
    if (!user) throw new Error('Usuario no autenticado');
    return await user.getIdToken();
};

// Mock service functions - replace with actual API calls
const getTags = async () => {
    const token = await getAuthToken();
    const response = await fetch('/api/tags', {
        headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Error fetching tags');
    return response.json();
};

const createTag = async (data) => {
    const token = await getAuthToken();
    const response = await fetch('/api/tags', {
        method: 'POST',
        headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Error creating tag');
    return response.json();
};

const deleteTag = async (id) => {
    const token = await getAuthToken();
    const response = await fetch(`/api/tags/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Error deleting tag');
    return response.json();
};

const TagsManager = () => {
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newTagName, setNewTagName] = useState('');
    const [newTagColor, setNewTagColor] = useState('blue');

    const colors = [
        { id: 'blue', class: 'bg-blue-100 text-blue-700' },
        { id: 'emerald', class: 'bg-emerald-100 text-emerald-700' },
        { id: 'purple', class: 'bg-purple-100 text-purple-700' },
        { id: 'orange', class: 'bg-orange-100 text-orange-700' },
        { id: 'red', class: 'bg-red-100 text-red-700' },
        { id: 'indigo', class: 'bg-indigo-100 text-indigo-700' },
        { id: 'pink', class: 'bg-pink-100 text-pink-700' },
        { id: 'slate', class: 'bg-slate-100 text-slate-700' },
    ];

    useEffect(() => {
        loadTags();
    }, []);

    const loadTags = async () => {
        setLoading(true);
        try {
            const data = await getTags();
            setTags(data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!newTagName.trim()) return;
        try {
            await createTag({ Name: newTagName, Color: newTagColor });
            setNewTagName('');
            loadTags();
        } catch (error) {
            alert('Error: ' + error.message);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Eliminar esta etiqueta?')) {
            try {
                await deleteTag(id);
                loadTags();
            } catch (error) {
                alert('Error: ' + error.message);
            }
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-slate-800">Gestión de Etiquetas</h2>
                    <p className="text-slate-500">Administre las etiquetas disponibles para clasificar clientes y campañas.</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Create Form */}
                <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm h-fit">
                    <h3 className="font-bold text-lg text-slate-800 mb-4">Nueva Etiqueta</h3>
                    <form onSubmit={handleCreate} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Nombre</label>
                            <input
                                type="text"
                                required
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                value={newTagName}
                                onChange={e => setNewTagName(e.target.value)}
                                placeholder="Ej: VIP"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Color</label>
                            <div className="flex flex-wrap gap-2">
                                {colors.map(color => (
                                    <button
                                        key={color.id}
                                        type="button"
                                        onClick={() => setNewTagColor(color.id)}
                                        className={`w-8 h-8 rounded-full border-2 transition-all ${color.class} ${
                                            newTagColor === color.id ? 'border-slate-600 scale-110' : 'border-transparent hover:scale-105'
                                        }`}
                                    />
                                ))}
                            </div>
                        </div>
                        <button
                            type="submit"
                            className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                        >
                            <Plus size={20} /> Crear Etiqueta
                        </button>
                    </form>
                </div>

                {/* List */}
                <div className="md:col-span-2 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                    <h3 className="font-bold text-lg text-slate-800 mb-4">Etiquetas Existentes</h3>
                    {loading ? (
                        <div className="text-center py-8 text-slate-400">Cargando...</div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {tags.map(tag => {
                                const colorClass = colors.find(c => c.id === tag.Color)?.class || 'bg-slate-100 text-slate-700';
                                return (
                                    <div key={tag.TagId} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:border-slate-200 hover:shadow-sm transition-all">
                                        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${colorClass}`}>
                                            <Tag size={14} />
                                            {tag.Name}
                                        </div>
                                        <button
                                            onClick={() => handleDelete(tag.TagId)}
                                            className="text-slate-400 hover:text-red-500 p-1 rounded-md hover:bg-red-50 transition-colors"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                );
                            })}
                            {tags.length === 0 && (
                                <div className="col-span-2 text-center py-8 text-slate-400 border border-dashed border-slate-200 rounded-lg">
                                    No hay etiquetas creadas.
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TagsManager;
